/**
 * ui/overlays.tsx — Dialog, ยืนยันการลบ, Toast
 *
 * ใช้ <dialog> ของ HTML แทนการทำ modal เอง
 * ได้ focus trap, ปิดด้วย Esc และ aria ที่ถูกต้องมาให้ฟรีจากเบราว์เซอร์
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '~/lib/cn';
import { Button } from './primitives';

// ==================== Dialog ====================

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** ความกว้างสูงสุด ค่าเริ่มต้นพอสำหรับฟอร์มสั้น */
  size?: 'sm' | 'md' | 'lg';
  footer?: ReactNode;
  children: ReactNode;
}

const SIZE_CLASS = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
} as const;

export function Dialog({
  open,
  onClose,
  title,
  description,
  size = 'sm',
  footer,
  children,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  // ปุ่ม Esc ทำให้ <dialog> ยิง event cancel — ต้องบอก parent ให้ปิดตาม
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    element.addEventListener('cancel', handleCancel);
    return () => element.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      className={cn(
        // dialog แบบ native ต้องกำหนด margin: auto เองเสมอ
        // ไม่งั้นบางเบราว์เซอร์ (โดยเฉพาะเมื่อใช้ CSS reset อย่าง Tailwind) จะวางชิดซ้ายบนแทนกึ่งกลางจอ
        'm-auto w-[calc(100vw-2rem)] rounded-lg border border-border-base bg-surface p-0 text-fg shadow-lg',
        'backdrop:bg-[var(--overlay)]',
        SIZE_CLASS[size],
      )}
      aria-labelledby="dialog-title"
    >
      <div className="flex items-start justify-between gap-4 border-b border-border-base px-4 py-3">
        <div className="min-w-0">
          <h2 id="dialog-title" className="text-sm font-semibold">
            {title}
          </h2>
          {description ? <p className="mt-0.5 text-xs text-fg-muted">{description}</p> : null}
        </div>
        <Button variant="ghost" iconOnly size="sm" onClick={onClose} aria-label="ปิด">
          <X size={16} />
        </Button>
      </div>

      <div className="max-h-[70vh] overflow-y-auto px-4 py-4">{children}</div>

      {footer ? (
        <div className="flex justify-end gap-2 border-t border-border-base px-4 py-3">{footer}</div>
      ) : null}
    </dialog>
  );
}

// ==================== ยืนยันการทำรายการที่ย้อนกลับไม่ได้ ====================

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  loading?: boolean;
  tone?: 'danger' | 'primary';
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'ยืนยัน',
  loading = false,
  tone = 'danger',
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose} disabled={loading}>
            ยกเลิก
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-fg-muted">{message}</p>
    </Dialog>
  );
}

// ==================== Toast ====================

type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_DURATION_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { id, tone, message }]);
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, TOAST_DURATION_MS);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message) => push('success', message),
      error: (message) => push('error', message),
      info: (message) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
        aria-live=polite ให้ screen reader อ่านข้อความที่โผล่มาโดยไม่ขัดจังหวะผู้ใช้
      */}
      <div
        className="pointer-events-none fixed right-4 bottom-4 z-50 flex w-80 flex-col gap-2"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast }: { toast: Toast }) {
  const config = {
    success: { icon: CheckCircle2, className: 'border-l-success' },
    error: { icon: XCircle, className: 'border-l-danger' },
    info: { icon: Info, className: 'border-l-accent' },
  }[toast.tone];

  const Icon = config.icon;

  return (
    <div
      className={cn(
        'pointer-events-auto flex items-start gap-2.5 rounded-md border border-border-base border-l-3 bg-surface px-3 py-2.5 shadow-md',
        config.className,
      )}
    >
      <Icon
        size={16}
        className={cn(
          'mt-0.5 shrink-0',
          toast.tone === 'success' && 'text-success',
          toast.tone === 'error' && 'text-danger',
          toast.tone === 'info' && 'text-accent',
        )}
        aria-hidden
      />
      <p className="text-xs leading-relaxed text-fg">{toast.message}</p>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast ต้องอยู่ภายใน ToastProvider');
  return context;
}

// ==================== แถบเตือนแบบ inline ที่ต้องมี icon ====================

export function WarningBanner({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2.5 text-xs text-warning">
      <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden />
      <div>{children}</div>
    </div>
  );
}
